import * as actionTypes from "../actionTypes";
import * as binderAlertRates from "../enums/binderAlertRates";
import * as binderErrors from "../enums/binderErrors";
import * as binders from "../lib/api/binders";
import * as bindersActions from "./bindersActions";
import find from "lodash.find";
import flatten from "lodash.flatten";
import uniq from "lodash.uniq";
import uuid from "uuid";

export function shareBinderWithUsers(
	binderId,
	shareIds,
	unshareIds,
	alertRate,
	alertHistory,
	message,
) {
	return (dispatch, getState) => {
		const state = getState();
		const binder = state.binders[binderId];

		if (!binder) {
			if (__DEV__) {
				console.warn("Attempting to share/alert a binder that cannot be found");
			}
			return Promise.reject(binderErrors.BINDER_NOT_FOUND);
		}

		if ((!shareIds || !shareIds.length) && (!unshareIds || !unshareIds.length)) {
			if (__DEV__) {
				console.warn("Attempting to share/unshare a binder with no new users.");
			}
			return Promise.reject(binderErrors.BINDER_INVALID_OPERATION);
		}

		const currentUser = state.session.currentUser;
		if (!currentUser) {
			if (__DEV__) {
				console.warn("You must be logged in to share/alert a binder.");
			}
			return Promise.reject(new Error(binderErrors.BINDER_UNAUTHORIZED_OPERATION));
		}

		const setAlert =
			alertRate !== null &&
			alertRate !== undefined &&
			alertHistory !== null &&
			alertHistory !== undefined;

		if (!setAlert) {
			const sharePromise = shareIds.length
				? binders.share(binderId, shareIds, message, binder.type, binder.libraryName)
				: Promise.resolve([]);

			return sharePromise
				.then(apiObservers => {
					return unshareIds && unshareIds.length
						? binders.unshare(binderId, unshareIds, binder.type, binder.libraryName)
						: apiObservers;
				})
				.then(apiObservers => {
					const oldObserverIds = [...binder.observerIds];
					const { observers } = dispatch(addObservers(apiObservers));
					const observerIds = observers.map(o => o.observerId);
					dispatch(bindersActions.setBinderObservers(binder.id, observerIds));
					dispatch(removeObservers(oldObserverIds));
				});
		}

		const alertBinderForUser = userId =>
			binders.setAlert(
				userId,
				binder.type,
				binderId,
				alertRate,
				alertHistory,
				binder.libraryName,
			);

		const lastShareIdIndex = shareIds.length - 1;
		const promises = shareIds.length
			? shareIds.slice(0, lastShareIdIndex).map(id => alertBinderForUser(id))
			: [];
		return Promise.all(promises)
			.then(() => {
				return shareIds.length ? alertBinderForUser(shareIds[lastShareIdIndex]) : [];
			})
			.then(apiObservers => {
				return unshareIds && unshareIds.length
					? binders.unshare(binderId, unshareIds, binder.type, binder.libraryName)
					: apiObservers;
			})
			.then(apiObservers => {
				const loggedInUserUpdated = !!find(shareIds, id => id === currentUser.id);
				if (loggedInUserUpdated) {
					dispatch(bindersActions.setBinderAlertHistory(binderId, alertHistory));
					dispatch(bindersActions.setBinderAlertRate(binderId, alertRate));
				}

				const oldObserverIds = [...binder.observerIds];
				const { observers } = dispatch(addObservers(apiObservers));
				const observerIds = observers.map(o => o.observerId);
				dispatch(bindersActions.setBinderObservers(binder.id, observerIds));
				dispatch(removeObservers(oldObserverIds));
			});
	};
}

export function shareBindersWithUser(binderIds, userId) {
	return (dispatch, getState) => {
		if (!Number.isFinite(userId)) {
			if (__DEV__) {
				console.warn("Attempting to share a binder with an invalid user.");
			}
			return Promise.reject(new Error(binderErrors.BINDER_INVALID_OPERATION));
		}

		const state = getState();

		const currentUser = state.session.currentUser;
		if (!currentUser) {
			if (__DEV__) {
				console.warn("You must be logged in to share a binder.");
			}
			return Promise.reject(new Error(binderErrors.BINDER_UNAUTHORIZED_OPERATION));
		}

		const shareableBinders = binderIds
			.map(id => state.binders[id])
			.filter(binder => binder && binder.shareable);
		if (!shareableBinders.length) {
			if (__DEV__) {
				console.warn(
					"Attempting to share binders that the current user does not have permission to share.",
				);
			}
			return Promise.reject(new Error(binderErrors.BINDER_UNAUTHORIZED_OPERATION));
		}

		const unsharedBinders = shareableBinders.filter(binder => {
			const observers = binder.observerIds.map(id => state.binderObservers[id]);
			const existingObserver = find(observers, ob => ob.userId === userId);
			return !existingObserver;
		});

		if (!unsharedBinders.length) {
			if (__DEV__) {
				console.warn(`Binders that can be shared are already shared with user ${userId}.`);
			}
			return Promise.reject(new Error(binderErrors.BINDER_UNAUTHORIZED_OPERATION));
		}

		return Promise.map(unsharedBinders, binder =>
			binders.share(binder.id, [userId], undefined, binder.type, binder.libraryName),
		).then(sharedApiObservers => {
			sharedApiObservers.forEach((apiObservers, idx) => {
				const binder = shareableBinders[idx];
				const apiObserver = find(apiObservers, ob => ob.user_id === userId);

				if (!apiObserver) {
					if (__DEV__) {
						console.warn(`Failed to share binder ${binder.id} with user ${userId}.`);
					}
				} else {
					const { observers } = dispatch(addObservers([apiObserver]));
					const [observerId] = observers.map(o => o.observerId);
					dispatch(bindersActions.addBinderObserver(binder.id, observerId));
				}
			});
		});
	};
}

// Updates users with provided ids to have specified alertRate and alertHistory.
export function changeAlertRequest(binderId, userIds, alertRate, alertHistory) {
	return (dispatch, getState) => {
		const state = getState();
		const binder = state.binders[binderId];

		if (!binder) {
			if (__DEV__) {
				console.warn("Attempting to alert a binder that cannot be found");
			}
			return Promise.reject(new Error(binderErrors.BINDER_NOT_FOUND));
		}

		if (!userIds || !userIds.length) {
			if (__DEV__) {
				console.warn("Attempting to alert a binder without specifying users.");
			}
			return Promise.reject(new Error(binderErrors.BINDER_INVALID_OPERATION));
		}

		if (
			alertRate === null ||
			alertRate === undefined ||
			alertHistory === null ||
			alertHistory === undefined
		) {
			if (__DEV__) {
				console.warn(
					"Attempting to alert a binder without specifying alert rate and alert history values.",
				);
			}
			return Promise.reject(new Error(binderErrors.BINDER_INVALID_OPERATION));
		}

		const currentUser = state.session.currentUser;
		if (!currentUser) {
			if (__DEV__) {
				console.warn("You must be logged in to alert a binder.");
			}
			return Promise.reject(new Error(binderErrors.BINDER_UNAUTHORIZED_OPERATION));
		}

		const currentObservers = binder.observerIds.map(id => state.binderObservers[id]);

		const alertBinderForUser = userId =>
			binders.setAlert(
				userId,
				binder.type,
				binderId,
				alertRate,
				alertHistory,
				binder.libraryName,
			);

		const lastUserIdIndex = userIds.length - 1;
		const promises = userIds.length
			? userIds.slice(0, lastUserIdIndex).map(id => alertBinderForUser(id))
			: [];
		return Promise.all(promises)
			.then(() => (userIds.length ? alertBinderForUser(userIds[lastUserIdIndex]) : []))
			.then(apiObservers => {
				const changedObservers = apiObservers.filter(apiObserver =>
					find(userIds, id => id === apiObserver.user_id),
				);

				const newObservers = changedObservers.filter(
					user => !find(currentObservers, co => co.userId === user.user_id),
				);
				const updatedObserverIds = changedObservers
					.map(user => {
						const observer = find(currentObservers, co => co.userId === user.user_id);
						return observer ? observer.__id : null;
					})
					.filter(id => id);

				if (newObservers.length) {
					const { observers } = dispatch(addObservers(newObservers));
					const observerIds = [
						...binder.observerIds,
						...observers.map(o => o.observerId),
					];
					dispatch(bindersActions.setBinderObservers(binderId, observerIds));
				}

				if (updatedObserverIds.length) {
					dispatch(updateObservers(updatedObserverIds, alertRate, alertHistory));
					const loggedInUserUpdated = find(
						updatedObserverIds,
						observerId => state.binderObservers[observerId].userId === currentUser.id,
					);
					if (loggedInUserUpdated) {
						dispatch(bindersActions.setBinderAlertHistory(binderId, alertHistory));
						dispatch(bindersActions.setBinderAlertRate(binderId, alertRate));
					}
				}
			});
	};
}

export function alertRequest(binderId, observerIds, alertRate, alertHistory) {
	return (dispatch, getState) => {
		const state = getState();

		if (!observerIds || !observerIds.length) {
			if (__DEV__) {
				console.warn("Attempting to alert a binder with no users specified.");
			}
			return Promise.reject(binderErrors.BINDER_INVALID_OPERATION);
		}

		const user = state.session.currentUser;
		if (!user) {
			if (__DEV__) {
				console.warn("You must be logged in to alert a binder");
			}
			return Promise.reject(binderErrors.BINDER_UNAUTHORIZED_OPERATION);
		}

		const binder = state.binders[binderId];
		if (!binder) {
			if (__DEV__) {
				console.warn("Attempting to share/alert a binder that cannot be found");
			}
			return Promise.reject(binderErrors.BINDER_NOT_FOUND);
		}

		const promises = observerIds.map(observerId => {
			const observer = state.binderObservers[observerId];
			return binders.setAlert(
				observer.userId,
				binder.type,
				binder.id,
				alertRate,
				alertHistory,
				binder.libraryName,
			);
		});
		return Promise.all(promises).then(() => {
			dispatch(updateObservers(observerIds, alertRate, alertHistory));
			if (find(observerIds, id => state.binderObservers[id].userId === user.id)) {
				dispatch(bindersActions.setBinderAlertHistory(binderId, alertHistory));
				dispatch(bindersActions.setBinderAlertRate(binderId, alertRate));
			}
		});
	};
}

export function addObservers(observers) {
	const mappedObservers = observers.map(o => ({
		alertHistory: o.alert_history,
		alertRate: o.alert_rate,
		email: o.email,
		firstName: o.first_name,
		lastName: o.last_name,
		observerId: uuid.v4(),
		userId: o.user_id,
	}));

	return {
		type: actionTypes.OBSERVER_ADD_MULTIPLE,
		observers: mappedObservers,
	};
}

export function addGroupedObservers(groupedObservers) {
	return dispatch => {
		const groupedWithIds = groupedObservers.map(observersList => {
			return observersList.map(o => ({
				alertHistory: o.alert_history,
				alertRate: o.alert_rate,
				email: o.email,
				firstName: o.first_name,
				lastName: o.last_name,
				observerId: uuid.v4(),
				userId: o.user_id,
			}));
		});
		const observers = flatten(groupedWithIds);

		dispatch({
			type: actionTypes.OBSERVER_ADD_MULTIPLE,
			observers,
		});

		return groupedWithIds;
	};
}

export function removeObservers(observerIds) {
	return {
		type: actionTypes.OBSERVER_REMOVE_MULTIPLE,
		observerIds,
	};
}

export function updateObserver(id, alertRate, alertHistory, email, fullName) {
	return {
		type: actionTypes.OBSERVER_UPDATE,
		id,
		alertRate,
		alertHistory,
		email,
		fullName,
	};
}

export function updateObservers(observerIds, alertRate, alertHistory) {
	return {
		type: actionTypes.OBSERVER_UPDATE_MULTIPLE,
		observerIds,
		alertRate,
		alertHistory,
	};
}

/******
The following thunk will only add an alert if:
1. The authenticated user is allowed to add the alert (owned OR binder is shareable).
   If the authenticated user is not permitted to add an alert to any of the binders, the promise is
   rejected with a BINDER_UNAUTHORIZED_OPERATION error.
2. The binder does not already have an alert for the user.
If the api request to share or alert a specific binder fails, the promise IS NOT rejected.
Instead, an alert is NOT added for that binder and the thunk attempts to add an alert for the
remaining binders.
******/
export function addAlertsRequest(binderIds, userId, alertRate) {
	return (dispatch, getState) => {
		if (!binderIds || !binderIds.length) {
			if (__DEV__) {
				console.warn("Attempting to add a user to no binders");
			}
			return Promise.reject(new Error(binderErrors.BINDER_INVALID_OPERATION));
		}

		if (
			userId === null ||
			userId === undefined ||
			alertRate === null ||
			alertRate === undefined
		) {
			if (__DEV__) {
				console.warn("userId and alertRate are required to add a user to an alert");
			}
			return Promise.reject(new Error(binderErrors.BINDER_INVALID_OPERATION));
		}

		const state = getState();

		const sessionUser = state.session.currentUser;
		if (!sessionUser) {
			if (__DEV__) {
				console.warn("You must be logged in to add an alert.");
			}
			return Promise.reject(new Error(binderErrors.BINDER_UNAUTHORIZED_OPERATION));
		}
		const filteredBinderIds = binderIds.filter(binderId => {
			const binder = state.binders[binderId];
			if (!binder) {
				return false;
			}
			return userId === sessionUser.id || binder.shareable;
		});
		const uniqueBinderIds = uniq(filteredBinderIds);

		if (!uniqueBinderIds.length) {
			if (__DEV__) {
				console.warn(
					"Current user cannot set the alert for other users on any selected binders.",
				);
			}
			return Promise.reject(new Error(binderErrors.BINDER_UNAUTHORIZED_OPERATION));
		}

		const alertPromises = uniqueBinderIds
			.map(id => {
				const binder = state.binders[id];
				const observers = binder.observerIds.map(oId => state.binderObservers[oId]);
				const observer = find(observers, ob => ob.userId === userId);

				if (observer && observer.alertRate !== binderAlertRates.NONE) {
					return null;
				}
				return binders
					.setAlert(userId, binder.type, binder.id, alertRate, false, binder.libraryName)
					.then(res => ({
						success: true,
						observerId: observer ? observer.__id : null,
						binderId: binder.id,
						apiUser: find(res, u => u.user_id === userId),
					}))
					.catch(err => {
						if (__DEV__) {
							console.warn("Unable to alert", binder.id, "for user", userId, err);
						}
						if (err && err.response && err.response.status === 400) {
							const failedRequest = {
								success: false,
								binderId: binder.id,
								message: "",
							};
							if (err.response.data) {
								if (typeof err.response.data === "string") {
									failedRequest.message = err.response.data;
								} else if (Array.isArray(err.response.data)) {
									failedRequest.message = err.response.data.join(" ");
								}
							}
							return failedRequest;
						}
						return null;
					});
			})
			.filter(setAlertRequest => !!setAlertRequest);

		return Promise.all(alertPromises).then(res => {
			const success = res.filter(data => data !== null && data.success && !!data.apiUser);

			// update existing observer alert rates
			const updatedObservers = success.filter(({ observerId }) => !!observerId);
			if (updatedObservers.length) {
				const updatedObserverIds = updatedObservers.map(({ observerId }) => observerId);
				dispatch(updateObservers(updatedObserverIds, alertRate, false));

				updatedObservers.forEach(({ apiUser, binderId }) => {
					if (apiUser && apiUser.user_id === sessionUser.id) {
						dispatch(bindersActions.setBinderAlertRate(binderId, alertRate));
					}
				});
			}

			// add new observers
			const newUsers = success.filter(({ observerId }) => !observerId);
			newUsers
				.filter(({ apiUser }) => !!apiUser)
				.forEach(({ apiUser, binderId }) => {
					if (apiUser.user_id === sessionUser.id) {
						dispatch(bindersActions.setBinderAlertRate(binderId, alertRate));
						return;
					}
					const { observers } = dispatch(addObservers([apiUser]));
					const observerIds = [
						...state.binders[binderId].observerIds,
						...observers.map(o => o.observerId),
					];
					dispatch(bindersActions.setBinderObservers(binderId, observerIds));
				});

			const successAlertedBinderIds = success.map(({ binderId }) => binderId);
			const failedRequests = res.filter(data => data !== null && !data.success);
			return { success: successAlertedBinderIds, fail: failedRequests };
		});
	};
}

// The following deletes the alert for the authenticated user. If that user can edit the binder, all
// alerts on that binder are deleted.
export function deleteAlertRequest(binderIds) {
	return (dispatch, getState) => {
		if (!binderIds || !binderIds.length) {
			if (__DEV__) {
				console.warn("Attempting to delete an alert from no binders.");
			}
			return Promise.reject(new Error(binderErrors.BINDER_INVALID_OPERATION));
		}

		const state = getState();

		const user = state.session.currentUser;
		if (!user) {
			if (__DEV__) {
				console.warn("You must be logged in to delete an alert.");
			}
			return Promise.reject(new Error(binderErrors.BINDER_UNAUTHORIZED_OPERATION));
		}

		const deleteBinders = binderIds.map(id => state.binders[id]).filter(binder => binder);
		if (!deleteBinders.length) {
			if (__DEV__) {
				console.warn("Cannot find any binders to delete alerts from");
			}
			return Promise.reject(new Error(binderErrors.BINDER_INVALID_OPERATION));
		}

		const deletePromises = [];
		deleteBinders.forEach(binder => {
			const alertedObserverIds = binder.observerIds
				.map(id => state.binderObservers[id])
				.filter(ob => ob.alertRate !== binderAlertRates.NONE && (binder.ownerUserId === user.id || ob.userId === user.id || user.group_admin || user.super_admin))
				.map(ob => ob.__id);

			if (!alertedObserverIds.length) {
				return;
			}

			deletePromises.push(
				dispatch(alertRequest(binder.id, alertedObserverIds, binderAlertRates.NONE, false)),
			);
		});

		return Promise.all(deletePromises);
	};
}

// The following removes the alert on all provided binders for the provided user.
export function removeAlertsRequest(binderIds, userId) {
	return (dispatch, getState) => {
		if (!binderIds || !binderIds.length) {
			if (__DEV__) {
				console.warn("Attempting to delete an alert from no binders.");
			}
			return Promise.reject(new Error(binderErrors.BINDER_INVALID_OPERATION));
		}

		if (userId === null || userId === undefined) {
			if (__DEV__) {
				console.warn("Attempting to delete an alert for no user.");
			}
			return Promise.reject(new Error(binderErrors.BINDER_INVALID_OPERATION));
		}

		const state = getState();

		const currentUser = state.session.currentUser;
		if (!currentUser) {
			if (__DEV__) {
				console.warn("You must be logged in to remove a user from an alert.");
			}
			return Promise.reject(new Error(binderErrors.BINDER_UNAUTHORIZED_OPERATION));
		}

		const filteredBinderIds = binderIds.filter(binderId => {
			const binder = state.binders[binderId];
			if (!binder) {
				return false;
			}
			return userId === currentUser.id || binder.shareable;
		});
		const uniqueBinderids = uniq(filteredBinderIds);

		if (!uniqueBinderids.length) {
			if (__DEV__) {
				console.warn(
					"Current user cannot remove the alert for other users on any selected binders.",
				);
			}
			return Promise.reject(new Error(binderErrors.BINDER_UNAUTHORIZED_OPERATION));
		}

		const removePromises = [];
		let removeCount = 0;
		uniqueBinderids.forEach(binderId => {
			const binder = state.binders[binderId];
			if (!binder) {
				return Promise.resolve();
			}

			const alertedObservers = binder.observerIds
				.map(id => state.binderObservers[id])
				.filter(ob => ob.alertRate !== binderAlertRates.NONE);
			const alertedObserver = find(alertedObservers, ob => ob.userId === userId);
			if (alertedObserver) {
				removeCount += 1;
				removePromises.push(
					dispatch(
						alertRequest(
							binder.id,
							[alertedObserver.__id],
							binderAlertRates.NONE,
							false,
						),
					),
				);
			}
		});

		return Promise.all(removePromises).then(() => removeCount);
	};
}

export function changeAlertFrequencyRequest(binderIds, alertRate, includeAllAlerts) {
	return (dispatch, getState) => {
		if (!binderIds || !binderIds.length) {
			if (__DEV__) {
				console.warn("Attempting to change the alert frequency for no binders.");
			}
			return Promise.reject(new Error(binderErrors.BINDER_INVALID_OPERATION));
		}

		if (!alertRate) {
			if (__DEV__) {
				console.warn("Must specify a new alert rate to change.");
			}
			return Promise.reject(new Error(binderErrors.BINDER_INVALID_OPERATION));
		}

		const state = getState();

		const sessionUser = state.session.currentUser;
		if (!sessionUser) {
			if (__DEV__) {
				console.warn("You must be logged in to change the frequency of an alert.");
			}
			return Promise.reject(new Error(binderErrors.BINDER_UNAUTHORIZED_OPERATION));
		}

		const selectedBinders = binderIds.map(id => state.binders[id]).filter(binder => !!binder);
		if (!selectedBinders.length) {
			if (__DEV__) {
				console.warn("Cannot find any binders to change alerts for.");
			}
			return Promise.reject(new Error(binderErrors.BINDER_UNAUTHORIZED_OPERATION));
		}

		const changeFreqPromises = [];
		let changeCount = 0;
		selectedBinders.forEach(binder => {
			const alertedObservers = binder.observerIds
				.map(id => state.binderObservers[id])
				.filter(
					ob =>
						ob.alertRate !== alertRate &&
						ob.alertRate !== binderAlertRates.NONE &&
						(ob.userId === sessionUser.id || includeAllAlerts),
				);

			if (!alertedObservers.length) {
				return;
			}

			const observerPromises = alertedObservers.map(observer => {
				return binders
					.setAlert(
						observer.userId,
						binder.type,
						binder.id,
						alertRate,
						observer.alertHistory,
						binder.libraryName,
					)
					.then(() => observer.__id)
					.catch(err => {
						if (__DEV__) {
							console.warn(
								"Unable to alert",
								binder.id,
								"for user",
								observer.userId,
								err,
							);
						}
						return null;
					});
			});

			changeFreqPromises.push(
				Promise.all(observerPromises).then(updatedObservers => {
					const observerIds = updatedObservers.filter(id => id);
					changeCount += observerIds.length;
					dispatch(updateObservers(observerIds, alertRate));
				}),
			);
		});

		return Promise.all(changeFreqPromises).then(() => changeCount);
	};
}
